import { computed, onScopeDispose, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import type { NotificationCategory, NotificationItem, NotificationSettingsResponse } from '@yana/shared';
import {
  listNotifications, markAllNotificationsRead, markNotificationRead, saveNotificationSettings,
} from '@/api/notifications';
import { ApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useToastStore } from '@/stores/toast';
import { useListQuery } from '@/composables/useListQuery';

/** The inbox lives only as long as the header, and is cleared on every account change. */
export function useNotifications() {
  const auth = useAuthStore();
  const toasts = useToastStore();
  const unreadCount = ref(0);
  const canViewLogins = ref(false);
  const preferences = ref<NotificationSettingsResponse['notifications']>({ rotationEnabled: true });
  const pending = ref(false);
  const open = ref(false);
  const identity = computed(() => `${auth.user?.id ?? ''}:${auth.user?.role ?? ''}`);
  let generation = 0;
  let disposed = false;

  const list = useListQuery<NotificationItem, { category: NotificationCategory; unreadOnly: boolean }>({
    pageSize: 8,
    immediate: false,
    filters: { category: 'all', unreadOnly: false },
    async load({ limit, offset, filters }) {
      if (!auth.user) return { items: [], total: 0 };
      const requestGeneration = ++generation;
      const response = await listNotifications({ ...filters, limit, offset });
      if (disposed || requestGeneration !== generation) return { items: [], total: 0 };
      unreadCount.value = response.unreadCount;
      canViewLogins.value = response.canViewLogins;
      preferences.value = response.notifications;
      // The server enforces visibility. This guard also avoids rendering stale login
      // entries if an account is switched while the popover is open.
      return { items: response.items.filter((item) => item.type !== 'new_device' || response.canViewLogins), total: response.total };
    },
  });

  watch(identity, () => {
    generation++;
    open.value = false;
    unreadCount.value = 0;
    canViewLogins.value = false;
    preferences.value = { rotationEnabled: true };
    list.items.value = [];
    list.total.value = 0;
    list.offset.value = 0;
    list.filters.value = { category: 'all', unreadOnly: false };
  }, { immediate: true, flush: 'sync' });

  watch(open, (value) => { if (value) void list.refresh(); });
  function refresh(): void {
    if (auth.user && !document.hidden && !list.loading.value) void list.refresh();
  }
  const timer = setInterval(refresh, 30_000);
  useEventListener(window, 'focus', refresh);
  useEventListener(document, 'visibilitychange', refresh);
  useEventListener(window, 'notification-settings-changed', (event) => {
    const settings = (event as CustomEvent<NotificationSettingsResponse>).detail;
    if (!settings) return;
    generation++;
    preferences.value = { ...settings.notifications };
    canViewLogins.value = settings.canViewLogins;
    // The count covers every page: subtracting only the visible rows would still
    // count hidden events on other pages. Clear until the fresh total arrives.
    list.items.value = [];
    list.total.value = 0;
    unreadCount.value = 0;
    list.filters.value = { ...list.filters.value, category: 'all' };
  });
  onScopeDispose(() => {
    disposed = true;
    generation++;
    clearInterval(timer);
  });

  async function read(item: NotificationItem): Promise<boolean> {
    if (item.readAt || pending.value) return !pending.value;
    pending.value = true;
    try {
      await markNotificationRead(item.id);
      await list.refresh();
      return true;
    } catch (error) {
      toasts.error('Не удалось отметить уведомление', error instanceof ApiError ? error.message : 'Повторите позже');
      return false;
    } finally { pending.value = false; }
  }

  async function readAll(): Promise<void> {
    if (pending.value || !unreadCount.value) return;
    pending.value = true;
    try {
      await markAllNotificationsRead();
      await list.refresh();
    } catch (error) {
      toasts.error('Не удалось отметить уведомления', error instanceof ApiError ? error.message : 'Повторите позже');
    } finally { pending.value = false; }
  }

  async function hideCategory(category: 'rotation' | 'new_device'): Promise<void> {
    if (!canViewLogins.value || pending.value) return;
    pending.value = true;
    try {
      const next = { ...preferences.value, [category === 'rotation' ? 'rotationEnabled' : 'newDeviceEnabled']: false };
      await saveNotificationSettings(next);
    } catch (error) {
      toasts.error('Не удалось скрыть вкладку', error instanceof ApiError ? error.message : 'Повторите позже');
    } finally { pending.value = false; }
  }

  return { ...list, unreadCount, canViewLogins, preferences, open, pending, read, readAll, hideCategory };
}
